import { Input, Button, VStack } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { FormField } from '../../../components/FormField'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import ExperienceFeedbackService from '../../../services/ExperienceFeedback/ExperienceFeedbackService'
import { IExperienceFeedbackStorePayload } from '../../../services/ExperienceFeedback/interfaces'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { UserService, IUser } from '../../../services/User/UserService'

export default function ExperienceFeedbackNewPage() {
  const toast = useAppToast()
  const experienceFeedbackService = useMemo(() => new ExperienceFeedbackService(), [])
  const userService = useMemo(() => new UserService(), [])
  const sectorService = useMemo(() => new SectorService(), [])
  const [sectors, setSectors] = useState<ISector[]>([])
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})

  const navigate = useNavigate()

  const [form, setForm] = useState<IExperienceFeedbackStorePayload>({
    name: '',
    email: '',
    sector_id: 0,
    manager: ''
  })

  const [managers, setManagers] = useState<IUser[]>([])

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const managersData = await userService.getManagers()
        setManagers(managersData)
      } catch (error) {
        toast.error(
          'Erro ao carregar gestores',
          'Não foi possível carregar a lista de gestores. Tente novamente mais tarde.'
        )
      }
    }

    const loadSectors = async () => {
      try {
        await sectorService.listForSelect().then((data) => {
          setSectors(data)
        })
      } catch {
        toast.error('Falha ao carregar setores', 'Ocorreu um erro ao buscar os setores. Tente novamente mais tarde.')
        setSectors([])
      }
    }

    loadManagers()
    loadSectors()
  }, [])

  const handleSubmit = async () => {
    setErrors({})
    try {
      const response = await experienceFeedbackService.store(form)
      navigate(`/experience-feedback/${response.data.experience_feedback.id}`)
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ errors: Record<string, string[]> }>
      toast.error('Erro ao criar a Avaliação de Experiência!', error)
      if (axiosError.response?.data?.errors) {
        setErrors(axiosError.response.data.errors)
      }
    }
  }

  return (
    <PublicFormLayout title="Avaliação de Experiência">
      <form>
        <VStack spacing="4">
          <FormField label="Nome do liderado" isRequired error={errors.name}>
            <Input type="text" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </FormField>
          <FormField label="E-mail do liderado" isRequired error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </FormField>

          <FormField label="Setor" isRequired error={errors.sector_id}>
            <FormSelect
              value={form.sector_id}
              onChange={(value) => setForm({ ...form, sector_id: Number(value) })}
              options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
              placeholder="Selecione uma opção"
              emptyValue={'0'}
            />
          </FormField>
          <FormField label="Gestor(a) imediato(a)" isRequired error={errors.manager}>
            <FormSelect
              value={form.manager}
              onChange={(value) => setForm({ ...form, manager: value })}
              options={managers.map((manager) => ({ value: manager.name, label: manager.name }))}
              placeholder="Selecione um gestor"
            />
          </FormField>
          <Button colorScheme="magenta" onClick={handleSubmit}>
            Iniciar Avaliação de Experiência
          </Button>
        </VStack>
      </form>
    </PublicFormLayout>
  )
}
